import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HouseholdService } from 'app/services/household-service';
import { OregonWellDataService } from 'app/services/oregon-well-data.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { FormControl, FormGroup } from '@angular/forms';
import { IHousehold } from 'app/models/household/household';
import { HousingUnitService } from 'app/services/housingunit-service';
import { IHousingUnit } from 'app/models/housingunits/housing-unit';
import { HouseholdSearch } from 'app/models/household/household-search';
import { HouseholdAssociations } from 'app/models/household/household-associations';

/*
 * Associate Household To Housing Unit Dialog
 */
@Component({
  selector: 'dialog-associate-household-housingunit',
  templateUrl: 'associate-household-housingunit-dialog.html',
  styleUrls: ['./associate-household-housingunit-dialog.scss']
})
export class DialogAssociateHouseholdToHousingUnitComponent implements OnInit {
  pageSize = 20;
  loading: boolean = false;
  applicationId: number = 0;
  housingUnit: IHousingUnit;
  housingUnits: IHousingUnit[] = [];
  householdSearchResults: IHousehold[] = [];
  searchResultsCount: number = 0;
  selectedHouseholdId: number = 0;
  flatResults: IHousehold[] = [];

  association: HouseholdAssociations = {
    household: null,
    housingUnits: [],
    wells: [],
    householdId: 0,
    housingUnitId: 0,
    housingUnitStartDate: '',
    housingUnitEndDate: '',
    createNewHousingUnit: false,
    createNewHousehold: false,
    newContactName: '',
    newAddress1: '',
    newCity: '',
    newZip: '',
    newCounty: ''
  }

  householdSearchForm = new FormGroup({
    contactName: new FormControl(''),
    county: new FormControl(''),
    address1: new FormControl(''),
    city: new FormControl(''),
    zip: new FormControl('')
  });

  householdSearch: HouseholdSearch = {
    id: '',
    contactName: '',
    address1: '',
    city: '',
    zip: '',
    searchBy: 'Household',
    searchText: '',
    county: '',
    searchResultsMaxRows: '',
    pregnancyInHousehold: false,
    infantsUnder1: false,
    age1to4: false,
    age5to9: false,
    age10to14: false,
    age15to19: false,
    age20to29: false,
    age30to39: false,
    age40to49: false,
    age50to59: false,
    age60to69: false,
    age70to79: false,
    age80Plus: false,
    age15AndBelow: false,
    age16AndAbove: false,
    infantsInHousehold: false,
    waterTreamentProvided: false,
    bottledWaterProvided: false
  }

  householdForm = new FormGroup({
    createNewHousehold: new FormControl({ value: false, disabled: false })
  });

  displayedColumnsHouseholds: string[] = ['action', 'id', 'contactName', 'address1', 'city', 'zip', 'county'];
  dataSourceHouseholds = new MatTableDataSource<IHousehold>(this.householdSearchResults);
  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;

  filterTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.dataSourceHouseholds.filter = value.trim().toLowerCase();
  }

  constructor(public householdService: HouseholdService,
    public oregonWellDataService: OregonWellDataService,
    public housingUnitService: HousingUnitService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: HouseholdAssociations,
    private dialogRef: MatDialogRef<DialogAssociateHouseholdToHousingUnitComponent>) {
    this.loading = true;
    this.association = this.data;
    this.loading = false;
  }

  ngOnInit(): void {
    this.load();
  }

  load() {
    if (this.association.housingUnitId > 0) {
      this.loading = true;
      this.housingUnitService.getHousingUnitById(this.association.housingUnitId).subscribe(result => {
        this.housingUnit = result;
        this.householdSearchForm.controls['contactName'].setValue(this.housingUnit.contactName);
        this.householdSearchForm.controls['address1'].setValue(this.housingUnit.address1);
        this.householdSearchForm.controls['city'].setValue(this.housingUnit.city);
        this.householdSearchForm.controls['zip'].setValue(this.housingUnit.zip);
        this.householdSearchForm.controls['county'].setValue(this.housingUnit.county);

        this.searchHouseholds();

        this.loading = false;
      },
        error => {
          this.oregonWellDataService.onHttpError(error);
          this.loading = false;
        }
      );
    }

    this.loading = false;
  }

  searchHouseholds() {
    this.setHouseholdSearchFieldsFromForm();

    if (!this.validHouseholdSearch()) {
      return;
    }

    this.loading = true;

    this.householdService.Search(this.householdSearch).subscribe(
      result => {
        this.householdSearchResults = result;
        this.loading = false;
      },
      error => {
        this.loading = false;
      },
      () => {
        this.flatResults = this.getFlatSearchResults();
        this.dataSourceHouseholds = new MatTableDataSource<IHousehold>(this.flatResults);
        this.dataSourceHouseholds.paginator = this.paginator;
        this.dataSourceHouseholds.sort = this.sort;
      }
    );
  }

  getFlatSearchResults(): any[] {
    return this.householdSearchResults.map((result: any) => {
      return {
        id: result.data?.id || null,
        contactName: result.data?.contactName || null,
        address1: result.data?.address1 || null,
        city: result.data?.city || null,
        zip: result.data?.zip || null,
        county: result.data?.county || null,
        householdId: result.data?.householdId || null,
      } as any;
    });
  }

  validHouseholdSearch(): boolean {
    if ((this.householdSearch.address1.length == 0) &&
      (this.householdSearch.city.length == 0) &&
      (this.householdSearch.zip.length == 0) &&
      (this.householdSearch.county.length == 0) &&
      (this.householdSearch.contactName.length == 0)) {
      this.toastr.error('Invalid Search.  Please refine');

      return false;
    }

    return true;
  }

  reset() {
    this.householdSearchForm.reset();
  }

  setHouseholdSearchFieldsFromForm() {
    this.householdSearch.county = this.householdSearchForm.value.county;
    this.householdSearch.address1 = this.householdSearchForm.value.address1;
    this.householdSearch.city = this.householdSearchForm.value.city;
    this.householdSearch.zip = this.householdSearchForm.value.zip;
    this.householdSearch.county = this.householdSearchForm.value.county;
    this.householdSearch.searchResultsMaxRows = '10';
  }

  /** Announce the change in sort state for assistive technology. */
  announceSortChange(sortState: Sort) { }

  onChangeSelectedHousehold(householdId: number) {
    this.selectedHouseholdId = householdId;
  }

  onChangeCreateNewHousehold() {
    if (this.association.createNewHousehold) {
      this.association.createNewHousehold = false;
    }
    else {
      this.association.createNewHousehold = true;
    }
  }

  associateHousingUnit() {
    this.loading = true;
    this.getFormValues();

    this.householdService.associateHouseholdToHousingUnit(this.association).subscribe(
      result => {
        this.toastr.success("Household/Housing Unit Associated");
        this.loading = false;
        this.dialogRef.close();
      },
      error => {
        this.toastr.error("Unable to Associate Household/Housing Unit");
        this.selectedHouseholdId = 0;
        this.loading = false;
      }
    );
  }

  getFormValues() {
    this.association.householdId = this.selectedHouseholdId > 0 ? this.selectedHouseholdId : this.association.householdId;

    this.association.createNewHousehold = this.householdForm.value.createNewHousehold;

    if (this.association.createNewHousehold) {
      this.association.newContactName = this.householdSearchForm.get('contactName').value;
      this.association.newAddress1 = this.householdSearchForm.get('address1').value;
      this.association.newCity = this.householdSearchForm.get('city').value;
      this.association.newZip = this.householdSearchForm.get('zip').value;
      this.association.newCounty = this.householdSearchForm.get('county').value;
    }
  }
}